import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { Observable, ReplaySubject, timer } from 'rxjs';
import { catchError, retry, share } from 'rxjs/operators';
import { BYPASS_RECAPTCHA_SESSION } from '../../constants/bypass-recaptcha-session.constant';
import { DEFAULT_RECAPTCHA_SESSION_RETRIES } from '../../constants/default-recaptcha-session-retries.constant';
import { AuthConfig } from '../../models/config/auth-config.interface';
import { AUTH_CONFIG } from '../../tokens/auth-config/auth-config.token';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly config: AuthConfig = inject(AUTH_CONFIG);
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly recaptchaV3Service: ReCaptchaV3Service =
    inject(ReCaptchaV3Service);

  private readonly recaptchaSessionTimeout = 4.5 * 60 * 1000;
  private readonly maxRetries: number =
    this.config.recaptchaSessionRetries ?? DEFAULT_RECAPTCHA_SESSION_RETRIES;

  readonly recaptchaSession$: Observable<void> = this.createRecaptchaSession();

  private createRecaptchaSession(): Observable<void> {
    return this.executeCreateSession().pipe(
      retry({
        count: this.maxRetries,
        delay: (error, retryCount) => {
          if (error.status !== 401) {
            throw error;
          }

          console.warn(
            `[AuthService] create-session failed with 401. Retrying with fresh reCAPTCHA token... (attempt ${retryCount} of ${this.maxRetries})`,
          );

          return this.recaptchaV3Service.execute('createSession');
        },
      }),
      catchError((error) => {
        console.error(
          `[AuthService] create-session failed after ${this.maxRetries} retries. Giving up.`,
          error,
        );
        throw error;
      }),
      share({
        connector: () => new ReplaySubject(1),
        resetOnComplete: () => timer(this.recaptchaSessionTimeout),
      }),
    );
  }

  private executeCreateSession(): Observable<void> {
    return this.httpClient.post<void>(
      `${this.config.authApi}/v2/create-session`,
      null,
      {
        context: new HttpContext().set(BYPASS_RECAPTCHA_SESSION, true),
        withCredentials: true,
      },
    );
  }
}
